/**
 * An in-memory stand-in for `@supabase/supabase-js` so the CRM runs with no
 * backend at all.
 *
 *   import { createFakeSupabase } from '~/demo/client/fakeSupabase';
 *   export const supabase = createFakeSupabase() as unknown as SupabaseClient<Database>;
 *
 * Rows come from the demo snapshot (`src/demo/data`, loaded lazily and
 * table-by-table). Writes mutate the in-memory copy for this session only; a
 * page reload restores the snapshot.
 *
 * What is faithful:
 *   - chainable / re-assignable builders that are real `PromiseLike`s
 *   - `select` with `count: 'exact'` (+ `head`), `range`, `limit`, stacked
 *     `order` (stable), `maybeSingle`
 *   - `eq neq in is gt gte lt lte like ilike`, three-arg `not`, and `or(...)`
 *     including nested `and(...)` groups and `*`/`%` LIKE wildcards
 *   - PostgREST embeds, including two levels, aliases, and `!inner` semantics
 *
 * What is approximated (see the notes at each site):
 *   - embed relationships are INFERRED from column names + data, not from the
 *     schema, since the generated types are erased at runtime
 *   - unknown tables resolve to `[]` instead of erroring
 *   - `signOut()` does not actually end the session
 *   - `single()` behaves like `maybeSingle()` (no "0 rows" error)
 */
import { createFakeAuth } from './auth';
import { FakeQuery } from './query';
import { createFakeStorage } from './storage';
import { DemoStore } from './store';
import type { FakeSupabaseOptions } from './types';

export type FakeSupabase = ReturnType<typeof createFakeSupabase>;

export function createFakeSupabase(options: FakeSupabaseOptions = {}) {
  const store = new DemoStore(options);

  const channel = (name: string) => {
    const ch = {
      topic: name,
      on: () => ch,
      subscribe: (cb?: (status: string) => void) => {
        if (cb) queueMicrotask(() => cb('SUBSCRIBED'));
        return ch;
      },
      unsubscribe: async () => 'ok' as const,
      send: async () => 'ok' as const,
    };
    return ch;
  };

  return {
    /** Any table name is accepted; ones the snapshot lacks read back empty. */
    from: (table: string) => new FakeQuery(store, table),
    /** No call sites today — resolves `{ data: null, error: null }`. */
    rpc: (_fn: string, _args?: unknown, _opts?: unknown) =>
      new FakeQuery(store, '__rpc__').nullResult(),
    schema: (_name: string) => ({ from: (table: string) => new FakeQuery(store, table) }),
    auth: createFakeAuth(),
    storage: createFakeStorage(),
    channel,
    getChannels: () => [],
    removeChannel: (_ch?: unknown) => Promise.resolve('ok' as const),
    removeAllChannels: () => Promise.resolve(['ok' as const]),
    /** Escape hatch for tests and for seeding extra rows. */
    _store: store,
  };
}

export { DEMO_SESSION, DEMO_USER, DEMO_USER_EMAIL, DEMO_USER_ID } from './auth';
export type { DemoSession, DemoUser } from './auth';
export { DemoStore } from './store';
export type { FakeSupabaseOptions, Row } from './types';
